import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Switch,
  Stepper,
  Step,
  StepLabel,
  Stack,
} from '@mui/material';

import { AppContext, Polygon, Point, Line } from '../../../context/context';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import Slider from '@mui/material/Slider';
import { alpha, type Theme } from '@mui/material/styles';
// Using TextField with datetime-local input instead of MUI DateTimePicker
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import { v4 as uuidv4 } from 'uuid';

import { 
  PeaksEditor, 
  RideTimeDistributionChart, 
  RideTypesSlider, 
  PassengerTypesEditor, 
  NumberOfPassengersEditor, 
  RidePatternEditor,
  PeakPresetSelector
} from './generate-rides';
import { Peak } from './generate-rides/types';
import { generateTimeLabels, weightedRandom, randomPointInPolygon, makePoint, weightedRandomTime, snapTimeToNearestLabel } from './generate-rides/utils';
import { detectCurrentPreset } from './generate-rides/presets';
import { logEvent } from '../../../services/analytics';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';
import { combineDateWithTime, extractTimeFromDateTime, isValidTimeFormat, parseTimeInput, formatDateAsWallTime } from '../../../utils/dateTimeUtils';
import { lineAddressPlaceholders } from '../../../utils/rides/addressPlaceholders';
import {
  frostedDrawerPaperSx,
  frostedDrawerHeaderSx,
  frostedDrawerCardSx,
  primaryPillButtonSx,
  secondaryPillButtonSx,
  destructivePillButtonSx,
  drawerActionButtonSx,
  drawerActionRowSx,
} from '../../../theme/componentStyles';
import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors } from '../../../theme/opsTokens';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

// Types
export type SimulationType = 'excel' | 'bulk' | 'p2p' | 'p2h';
export type RidesSource = 'layer' | 'upload' | 'generate';

interface GenerateRidesDrawerProps {
  open: boolean;
  onClose: () => void;
  modalLayers: { id: string; name: string; type: 'point' | 'line' }[];
  onGenerateRides: (config: any) => void;
}

const defaultPeak: Peak = { peak_time: '07:30', effect: 4, width_left: 3, width_right: 2 };

// Helper to convert 'HH:mm' to minutes
function toMinutes(t: string) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

// Add helper function for ride pattern logic
function getRidePatternState(selectedPolygonId: string, numHubs: number, ridePattern: {p2p: number, p2h: number, h2h: number}) {
  // console.log('getRidePatternState', { selectedPolygonId, numHubs, ridePattern });
  if (!selectedPolygonId && numHubs < 2) {
    return { pattern: { p2p: 0, p2h: 0, h2h: 0 }, disabled: true, onlyH2HDisabled: false };
  }
  if (!selectedPolygonId && numHubs >= 2) {
    return { pattern: { p2p: 0, p2h: 0, h2h: 100 }, disabled: true, onlyH2HDisabled: false };
  }
  if (selectedPolygonId && numHubs < 1) {
    return { pattern: { p2p: 100, p2h: 0, h2h: 0 }, disabled: true, onlyH2HDisabled: false };
  }
  if (selectedPolygonId && numHubs === 1) {
    const p2p = Math.max(0, ridePattern.p2p || 0);
    const p2h = Math.max(0, ridePattern.p2h || 0);
    const total = p2p + p2h;
    if (total <= 0) {
      return { pattern: { p2p: 50, p2h: 50, h2h: 0 }, disabled: false, onlyH2HDisabled: true };
    }
    const normalizedP2P = Math.round((p2p / total) * 100);
    return {
      pattern: { p2p: normalizedP2P, p2h: 100 - normalizedP2P, h2h: 0 },
      disabled: false,
      onlyH2HDisabled: true,
    };
  }
  // Polygon selected, 2+ hubs: user-editable
  let { p2p, p2h, h2h } = ridePattern;
  if (numHubs < 2) h2h = 0;
  let total = p2p + p2h + h2h;
  if (total !== 100) {
    // Normalize
    const scale = 100 / (total || 1);
    p2p = Math.round(p2p * scale);
    p2h = Math.round(p2h * scale);
    h2h = Math.round(h2h * scale);
  }
  return { pattern: { p2p, p2h, h2h }, disabled: false, onlyH2HDisabled: false };
}

const GenerateRidesDrawer: React.FC<GenerateRidesDrawerProps> = ({ open, onClose, modalLayers, onGenerateRides }) => {
  const { t } = useTranslation('mapCore');
  const context = useContext(AppContext);
  const global = context?.global;

  // Get all polygons for selection, memoized for performance
  const polygons = useMemo(
    () =>
      global
        ? global.mapLayers.flatMap(l =>
            (l.shapes || [])
              .filter((s): s is Polygon => s.type === 'polygon')
              .map(p => ({ id: p.id, name: p.name || p.id, createdAt: p.createdAt || 0 }))
          )
        : [],
    [global]
  );

  // Sort polygons by creation time (most recent first)
  const sortedPolygons = useMemo(() => {
    return [...polygons].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [polygons]);

  // Default polygon id to the most recently created polygon
  const defaultPolygonId = useMemo(() => {
    if (sortedPolygons.length === 0) return '';
    return sortedPolygons[0].id;
  }, [sortedPolygons]);

  // State for selected polygon, initialized to the most recent polygon
  const [selectedPolygonId, setSelectedPolygonId] = useState<string>(defaultPolygonId);

  const [isGenerating, setIsGenerating] = useState(false);

  // Update selected polygon when default changes (e.g., when new polygons are added)
  useEffect(() => {
    if (defaultPolygonId && defaultPolygonId !== selectedPolygonId) {
      setSelectedPolygonId(defaultPolygonId);
    }
  }, [defaultPolygonId, selectedPolygonId]);

  // Set default start and end dates: DEFAULT_SIMULATION_DATE at 6am and 10pm for consistency
  const defaultStartDate = useMemo(() => {
    const d = new Date(`${DEFAULT_SIMULATION_DATE}T06:00:00`);
    return d;
  }, []);
  const defaultEndDate = useMemo(() => {
    const d = new Date(`${DEFAULT_SIMULATION_DATE}T22:00:00`);
    return d;
  }, []);

  const [simType, setSimType] = useState<SimulationType>('bulk');
  const [rideGenParams, setRideGenParams] = useState<any>({
    hubWeights: [],
    peaks: [],
    numberOfRides: 100,
    startTime: '06:00',
    endTime: '22:00',
  });
  
  // Preset state management
  const [selectedPreset, setSelectedPreset] = useState<string>('commuter');
  const [accordionExpanded, setAccordionExpanded] = useState<boolean>(false);
  
  // Initialize peaks with commuter preset
  const [peaks, setPeaks] = useState<Peak[]>([
    { peak_time: '08:00', effect: 2.5, width_left: 2, width_right: 2 },
    { peak_time: '17:30', effect: 2.5, width_left: 2, width_right: 2 }
  ]);
  
  const [rideTypes, setRideTypes] = useState([
    { type: 'departure_fixed', probability: 0.5 },
    { type: 'arrival_fixed', probability: 0.5 },
  ]);
  const [passengerTypes, setPassengerTypes] = useState([
    { passenger_type: 'standard', processing_time: 30, probability: 0.98 },
    { passenger_type: 'wheelchair', processing_time: 90, probability: 0.02 },
  ]);
  const [numberOfPassengers, setNumberOfPassengers] = useState([
    { number_of_passengers: 1, probability: 0.95 },
    { number_of_passengers: 2, probability: 0.03 },
    { number_of_passengers: 3, probability: 0.02 },
  ]);
  const [tripsToHubsPercent, setTripsToHubsPercent] = useState<number>(0);
  const [ridePattern, setRidePattern] = useState({ p2p: 50, p2h: 30, h2h: 20 });
  const selectMenuProps = useMemo(
    () => ({
      PaperProps: { sx: { zIndex: 2605 } },
      sx: { zIndex: 2605 },
    }),
    []
  );
  
  // Meeting points state
  const [useMeetingPointsOnly, setUseMeetingPointsOnly] = useState<boolean>(false);
  const [selectedMeetingPointLayerId, setSelectedMeetingPointLayerId] = useState<string>('');

  // Preset change handler
  const handlePresetChange = (presetId: string, newPeaks: Peak[]) => {
    setSelectedPreset(presetId);
    setPeaks(newPeaks);
    
    // Auto-expand accordion for custom preset, collapse for others
    setAccordionExpanded(presetId === 'custom');
    
    // Track preset selection
    logEvent({
      category: 'TripGeneration',
      action: 'PeakPresetSelected',
      label: 'Peak Preset Changed',
      presetId: presetId,
      step: step
    });
  };

  // Centralized ride pattern logic (table-based)
  const numHubs = rideGenParams.hubWeights ? rideGenParams.hubWeights.length : 0;
  const ridePatternState = useMemo(
    () => getRidePatternState(selectedPolygonId, numHubs, ridePattern),
    [selectedPolygonId, numHubs, ridePattern]
  );

  // When hubs are added/removed, update tripsToHubsPercent accordingly
  useEffect(() => {
    if (rideGenParams.hubWeights && rideGenParams.hubWeights.length === 1) {
      setTripsToHubsPercent(50);
    } else if (!rideGenParams.hubWeights || rideGenParams.hubWeights.length === 0) {
      setTripsToHubsPercent(0);
    }
    // Do not auto-update if more than 1 hub (user can adjust manually)
  }, [rideGenParams.hubWeights]);

  // Detect preset changes when peaks are manually modified
  useEffect(() => {
    const detectedPreset = detectCurrentPreset(peaks);
    if (detectedPreset !== selectedPreset) {
      setSelectedPreset(detectedPreset);
    }
  }, [peaks, selectedPreset]);

  // Derived layer options
  const pointLayers = modalLayers.filter(l => l.type === 'point');
  const lineLayers = modalLayers.filter(l => l.type === 'line');
  
  // Get available meeting point layers (point layers that contain meeting points)
  const meetingPointLayers = useMemo(() => {
    if (!global) return [];
    
    return global.mapLayers
      .filter(layer => {
        // Check if layer contains points and has "Meeting Point" in the name or contains meeting point shapes
        const hasPoints = layer.shapes.some(shape => shape.type === 'point');
        const isMeetingPointLayer = layer.layerName.toLowerCase().includes('meeting point') ||
          layer.shapes.some(shape => 
            shape.type === 'point' && 
            (shape.name?.toLowerCase().includes('meeting point') || 
             shape.description?.toLowerCase().includes('meeting point'))
          );
        return hasPoints && isMeetingPointLayer;
      })
      .map(layer => ({
        id: layer.layerId.toString(),
        name: layer.layerName,
        pointCount: layer.shapes.filter(shape => shape.type === 'point').length
      }));
  }, [global]);

  // Track trip generation initiation when drawer opens
  React.useEffect(() => {
    if (open) {
      logEvent({
        category: 'TripGeneration',
        action: 'TripGenerationOpened',
        label: 'Trip Generation Drawer Opened',
        polygonCount: polygons.length,
        availablePointLayers: pointLayers.length,
        availableLineLayers: lineLayers.length,
        hasSelectedPolygon: !!selectedPolygonId,
        hubCount: numHubs
      });
    }
  }, [open, polygons.length, pointLayers.length, lineLayers.length, selectedPolygonId, numHubs]);

  // Helper to get meeting points from selected layer
  const getMeetingPoints = useMemo(() => {
    if (!useMeetingPointsOnly || !selectedMeetingPointLayerId || !global) return [];
    
    const selectedLayer = global.mapLayers.find(layer => 
      layer.layerId.toString() === selectedMeetingPointLayerId
    );
    
    if (!selectedLayer) return [];
    
    return selectedLayer.shapes.filter((shape): shape is Point => 
      shape.type === 'point'
    );
  }, [useMeetingPointsOnly, selectedMeetingPointLayerId, global]);

  // Helper to get a random meeting point
  const getRandomMeetingPoint = (): Point | null => {
    const meetingPoints = getMeetingPoints;
    if (meetingPoints.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * meetingPoints.length);
    return meetingPoints[randomIndex];
  };

  // Helper to check if a passenger type is wheelchair
  const isWheelchairPassenger = (passengerType: string): boolean => {
    return passengerType.toLowerCase().includes('wheelchair');
  };

  // Helper to generate time labels in 15-min intervals between two times
  function generateTimeLabels(start: string, end: string, interval: number = 15): string[] {
    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const toTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    let cur = toMinutes(start), endM = toMinutes(end);
    const times: string[] = [];
    while (cur <= endM) {
      times.push(toTime(cur));
      cur += interval;
    }
    return times;
  }

  // Replace getTime with a simple function for time strings
  const getTime = (t: string | null | undefined, fallback: string) => t && typeof t === 'string' ? t : fallback;
  const startTime = getTime(rideGenParams.startTime, '06:00');
  const endTime = getTime(rideGenParams.endTime, '22:00');
  const timeLabels = generateTimeLabels(startTime, endTime, 15);

  // Snap peaks to valid timeLabels if needed
  React.useEffect(() => {
    if (timeLabels.length === 0) {
      return;
    }

    setPeaks(prev => prev.map(peak => {
      if (!timeLabels.includes(peak.peak_time)) {
        return { ...peak, peak_time: snapTimeToNearestLabel(peak.peak_time, timeLabels) };
      }
      return peak;
    }));
    // eslint-disable-next-line
  }, [startTime, endTime]);

  // Add forceUpdate state to force re-render on hub changes
  const [, setForceUpdate] = useState(0);

  // HubsEditor
  const HubsEditor: React.FC<{ availablePoints: { id: string; name: string }[]; hubs: any[]; setHubs: (hubs: any[]) => void }> = ({ availablePoints, hubs, setHubs }) => {
    const [localHubs, setLocalHubs] = useState<any[]>(hubs);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const total = localHubs.reduce((sum, h) => sum + (h.percentage || 0), 0);
    const error = localHubs.length > 2 && Math.abs(total - 100) > 0.01;

    // Sync local state with prop changes
    useEffect(() => {
      setLocalHubs(hubs);
    }, [hubs]);

    const addHub = (pointId: string) => {
      if (!pointId) return;
      const point = availablePoints.find(p => p.id === pointId);
      if (!point || localHubs.some(h => h.id === point.id)) return;
      let newHubs: any[] = [...localHubs, { id: point.id, name: point.name, percentage: 0 }];
      if (newHubs.length === 1) {
        newHubs[0].percentage = 100;
      } else if (newHubs.length === 2) {
        newHubs = newHubs.map(() => ({ ...point, percentage: 50 }));
        newHubs[0].id = localHubs[0].id; newHubs[0].name = localHubs[0].name;
        newHubs[1].id = point.id; newHubs[1].name = point.name;
      }
      setLocalHubs(newHubs);
      setHubs(newHubs);
    };

    const addAllHubs = () => {
      // Get all available points that aren't already in the hubs list
      const remainingPoints = availablePoints.filter(p => !localHubs.some((h: any) => h.id === p.id));
      if (remainingPoints.length === 0) return;
      
      // Add all remaining points
      const newHubs: any[] = [...localHubs];
      remainingPoints.forEach(point => {
        newHubs.push({ id: point.id, name: point.name, percentage: 0 });
      });
      
      // Distribute percentages equally (100% / number of hubs)
      const equalPercentage = 100 / newHubs.length;
      // Round to 2 decimal places to avoid floating point issues
      const roundedPercentage = Math.round(equalPercentage * 100) / 100;
      
      // Distribute percentages, handling rounding to ensure sum is exactly 100%
      let totalAssigned = 0;
      newHubs.forEach((hub, idx) => {
        if (idx === newHubs.length - 1) {
          // Last hub gets the remainder to ensure total is exactly 100%
          hub.percentage = 100 - totalAssigned;
        } else {
          hub.percentage = roundedPercentage;
          totalAssigned += roundedPercentage;
        }
      });
      
      setLocalHubs(newHubs);
      setHubs(newHubs);
    };

    const handlePercentChange = (idx: number, value: string | number) => {
      let v = typeof value === 'string' ? parseFloat(value) : value;
      v = Math.max(0, Math.min(100, v));
      let newHubs = [...localHubs];
      if (localHubs.length === 2) {
        newHubs[idx].percentage = v;
        newHubs[1 - idx].percentage = 100 - v;
      } else {
        newHubs[idx].percentage = v;
      }
      
      // Update local state immediately for responsive UI
      setLocalHubs(newHubs);
      
      // Debounce the parent update to prevent focus loss
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(() => {
        setHubs(newHubs);
      }, 300);
    };

    const removeHub = (idx: number) => {
      let newHubs = localHubs.filter((_: any, i: number) => i !== idx);
      if (newHubs.length === 1) newHubs[0].percentage = 100;
      if (newHubs.length === 2) newHubs = newHubs.map(h => ({ ...h, percentage: 50 }));
      setLocalHubs(newHubs);
      setHubs(newHubs);
    };

    return (
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle1">{t('generateRidesDrawer.hubsEditor.title')}</Typography>
        <Box sx={{ ...drawerActionRowSx, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('generateRidesDrawer.hubsEditor.selectHubPoint')}</InputLabel>
            <Select
              value=""
              label={t('generateRidesDrawer.hubsEditor.selectHubPoint')}
              onChange={e => addHub(e.target.value as string)}
              MenuProps={selectMenuProps}
            >
              {availablePoints.filter(p => !localHubs.some((h: any) => h.id === p.id)).map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            onClick={addAllHubs} 
            disabled={availablePoints.filter(p => !localHubs.some((h: any) => h.id === p.id)).length === 0}
            sx={(theme) => ({
              ...secondaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('generateRidesDrawer.hubsEditor.addAllHubs')}
          </Button>
        </Box>
        {localHubs.map((hub: any, idx: number) => (
          <Box key={`hub-${hub.id}-${idx}`} display="flex" alignItems="center" gap={2} mb={1}>
            <Typography sx={{ minWidth: 120 }}>{hub.name}</Typography>
            <TextField
              label={t('generateRidesDrawer.hubsEditor.percentOfRides')}
              type="number"
              value={hub.percentage}
              onChange={e => handlePercentChange(idx, e.target.value)}
              inputRef={(el) => {
                inputRefs.current[idx] = el;
              }}
              size="small"
              sx={{ width: 100 }}
              inputProps={{ min: 0, max: 100 }}
              error={error}
            />
            <Button
              onClick={() => removeHub(idx)}
              sx={(theme) => ({
                ...destructivePillButtonSx(theme),
                ...drawerActionButtonSx,
              })}
            >
              {t('generateRidesDrawer.hubsEditor.remove')}
            </Button>
          </Box>
        ))}
        {error && <Typography color="error">{t('generateRidesDrawer.hubsEditor.sumError')}</Typography>}
      </Box>
    );
  };

  const buildConfig = () => ({
    simType,
    rideGenParams: {
      ...rideGenParams,
      peaks,
      selectedPolygonId,
      tripsToHubsPercent,
      ridePattern,
    },
  });

  const handleGenerateTrips = async () => {
    if (!global) return;
    setIsGenerating(true);
    // Yield to the browser for one frame so the loading overlay paints before
    // the synchronous generation loop blocks the main thread.
    await new Promise(resolve => setTimeout(resolve, 0));
    if (!selectedPolygonId) {
      const hubsNoPoly = rideGenParams.hubWeights || [];
      const numTripsNoPoly = rideGenParams.numberOfRides || 0;
      const startTimeStr = rideGenParams.startTime || '06:00';
      const endTimeStr = rideGenParams.endTime || '22:00';
      // Use timezone-agnostic datetime creation
      const startDateObj = new Date(combineDateWithTime(DEFAULT_SIMULATION_DATE, startTimeStr));
      const endDateObj = new Date(combineDateWithTime(DEFAULT_SIMULATION_DATE, endTimeStr));
      if (!startDateObj || !endDateObj) return;
      const pointsNoPoly: Point[] = [];
      const linesNoPoly: Line[] = [];
      for (let i = 0; i < numTripsNoPoly; i++) {
        // Pick two different hubs by weight
        let hub1: any, hub2: any;
        do {
          hub1 = weightedRandom(hubsNoPoly, hubsNoPoly.map((h: any) => h.percentage / 100));
          hub2 = weightedRandom(hubsNoPoly, hubsNoPoly.map((h: any) => h.percentage / 100));
        } while (hub1.id === hub2.id && hubsNoPoly.length > 1);
        // Find hub points in all layers
        const hubPoint1 = global.mapLayers.flatMap((l: any) => l.shapes).find((s: any) => s.type === 'point' && s.id === hub1.id);
        const hubPoint2 = global.mapLayers.flatMap((l: any) => l.shapes).find((s: any) => s.type === 'point' && s.id === hub2.id);
        if (!hubPoint1 || !hubPoint2) continue;
        const origin = makePoint(hubPoint1.coordinates, t('generateRidesDrawer.originLabel', { index: i + 1 }), '#FF0000');
        const destination = makePoint(hubPoint2.coordinates, t('generateRidesDrawer.destinationLabel', { index: i + 1 }), '#00FF00');
        // Request time
        const requestTime = weightedRandomTime(startDateObj, endDateObj, peaks);
        // Use the correct ride pattern for this trip
        const rideType = weightedRandom(rideTypes, rideTypes.map((r: any) => r.probability)).type;
        // Number of passengers
        const numPassengers = weightedRandom(numberOfPassengers, numberOfPassengers.map((g: any) => g.probability)).number_of_passengers;
        let standardSeat = 0;
        let wheelchair = 0;
        for (let j = 0; j < numPassengers; j++) {
          const type = weightedRandom(passengerTypes, passengerTypes.map((p: any) => p.probability)).passenger_type.toLowerCase();
          if (type.includes('wheelchair')) {
            wheelchair++;
          } else {
            standardSeat++;
          }
        }
        // Points must have unique IDs
        origin.id = uuidv4();
        destination.id = uuidv4();
        pointsNoPoly.push(origin, destination);
        // Line (trip)
        const rideId = t('generateRidesDrawer.tripLabel', { index: i + 1 });
        const userId = `user-${i + 1}`;
        
        const requestTimeString = requestTime instanceof Date
          ? formatDateAsWallTime(requestTime)
          : requestTime;
        
        const line: Line & { originPointId: string; destPointId: string } = {
          id: uuidv4(),
          type: 'line',
          name: rideId,
          ride_id: rideId,
          user_id: userId,
          colour: '#0000FF',
          description: '',
          opacity: '1',
          borderColour: '#0000FF',
          borderWidth: 2,
          isLocked: false,
          coordinates: [origin.coordinates, destination.coordinates],
          arrowStyle: 'arrow',
          time: requestTimeString,
          numPassengers,
          standard_seat: standardSeat,
          wheelchair: wheelchair,
          request_type: rideType,
          originPointId: origin.id,
          destPointId: destination.id,
          ...lineAddressPlaceholders({}, i + 1),
        };
        linesNoPoly.push(line);
      }
      // Add new layer
      const newLayerId = global.mapLayers.length > 0 ? Math.max(...global.mapLayers.map((l: any) => l.layerId)) + 1 : 0;
      const newLayer = {
        layerId: newLayerId,
        layerName: t('generateRidesDrawer.generatedLayerName', { index: newLayerId + 1 }),
        hidden: false,
        shapes: [...pointsNoPoly, ...linesNoPoly],
      };
      global.setLayers((prev: any) => [...prev, newLayer]);
      global.setActiveLayerId(newLayerId);
      
      // Track successful trip generation completion
      logEvent({
        category: 'TripGeneration',
        action: 'TripGenerationCompleted',
        label: 'Trip Generation Completed Successfully',
        polygonId: selectedPolygonId || 'none',
        polygonName: sortedPolygons.find(p => p.id === selectedPolygonId)?.name || 'none',
        numberOfRides: numTripsNoPoly,
        generatedPoints: pointsNoPoly.length,
        generatedLines: linesNoPoly.length,
        newLayerId: newLayerId,
        newLayerName: newLayer.layerName,
        hubCount: numHubs,
        ridePattern: ridePattern,
        peaksCount: peaks.length,
        startDate: combineDateWithTime(DEFAULT_SIMULATION_DATE, startTimeStr),
        endDate: combineDateWithTime(DEFAULT_SIMULATION_DATE, endTimeStr)
      });
      
      setIsGenerating(false);
      onClose();
      return;
    }
    const polygonParentLayer = global.mapLayers.find(l => l.shapes.some(s => s.type === 'polygon' && s.id === selectedPolygonId));
    if (!polygonParentLayer) return;
    const polygonCoords: [number, number][] = (polygonParentLayer.shapes.find(s => s.type === 'polygon' && s.id === selectedPolygonId) as Polygon).coordinates[0];
    const hubs: { id: string; name: string; coordinates: [number, number]; percentage: number }[] = rideGenParams.hubWeights || [];
    const numTrips: number = rideGenParams.numberOfRides || 0;
    const startTimeStr = rideGenParams.startTime || '06:00';
    const endTimeStr = rideGenParams.endTime || '22:00';
    // Use timezone-agnostic datetime creation
    const startDateObj = new Date(combineDateWithTime(DEFAULT_SIMULATION_DATE, startTimeStr));
    const endDateObj = new Date(combineDateWithTime(DEFAULT_SIMULATION_DATE, endTimeStr));
    if (!startDateObj || !endDateObj) return;
    const points: Point[] = [];
    const lines: Line[] = [];
    const effectiveRidePattern = ridePatternState.pattern;
    function pickPattern() {
      const rand = Math.random() * 100;
      if (rand < effectiveRidePattern.p2p) return 'p2p';
      if (rand < effectiveRidePattern.p2p + effectiveRidePattern.p2h) return 'p2h';
      return 'h2h';
    }
    for (let i = 0; i < numTrips; i++) {
      let origin: Point, destination: Point;
      const pattern = pickPattern();
      
      // Determine passenger types first to check for wheelchair passengers
      const numPassengers = weightedRandom(numberOfPassengers, numberOfPassengers.map(g => g.probability)).number_of_passengers;
      let standardSeat = 0;
      let wheelchair = 0;
      let hasWheelchairPassenger = false;
      
      for (let j = 0; j < numPassengers; j++) {
        const type = weightedRandom(passengerTypes, passengerTypes.map(p => p.probability)).passenger_type.toLowerCase();
        if (type.includes('wheelchair')) {
          wheelchair++;
          hasWheelchairPassenger = true;
        } else {
          standardSeat++;
        }
      }
      
      // Helper function to get origin/destination based on meeting points mode and wheelchair status
      const getOriginDestination = (isOrigin: boolean, isWheelchair: boolean = false) => {
        if (useMeetingPointsOnly && !isWheelchair) {
          // Use meeting points for non-wheelchair passengers
          const meetingPoint = getRandomMeetingPoint();
          if (meetingPoint) {
            return makePoint(meetingPoint.coordinates, isOrigin ? t('generateRidesDrawer.originLabel', { index: i + 1 }) : t('generateRidesDrawer.destinationLabel', { index: i + 1 }), isOrigin ? '#FF0000' : '#00FF00');
          }
        }
        
        // Use random polygon point for wheelchair passengers or when meeting points not enabled
        return makePoint(randomPointInPolygon(polygonCoords), isOrigin ? t('generateRidesDrawer.originLabel', { index: i + 1 }) : t('generateRidesDrawer.destinationLabel', { index: i + 1 }), isOrigin ? '#FF0000' : '#00FF00');
      };
      
      if (pattern === 'h2h' && hubs.length > 1) {
        // Pick two different hubs by weight
        let hub1: { id: string; name: string; coordinates: [number, number]; percentage: number };
        let hub2: { id: string; name: string; coordinates: [number, number]; percentage: number };
        do {
          hub1 = weightedRandom(hubs, hubs.map(h => h.percentage / 100));
          hub2 = weightedRandom(hubs, hubs.map(h => h.percentage / 100));
        } while (hub1.id === hub2.id && hubs.length > 1);
        const hubPoint1 = polygonParentLayer.shapes.find((s): s is Point => s.type === 'point' && s.id === hub1.id);
        const hubPoint2 = polygonParentLayer.shapes.find((s): s is Point => s.type === 'point' && s.id === hub2.id);
        if (!hubPoint1 || !hubPoint2) continue;
        origin = makePoint(hubPoint1.coordinates, t('generateRidesDrawer.originLabel', { index: i + 1 }), '#FF0000');
        destination = makePoint(hubPoint2.coordinates, t('generateRidesDrawer.destinationLabel', { index: i + 1 }), '#00FF00');
      } else if (pattern === 'p2h' && hubs.length > 0) {
        const hubWeight = weightedRandom(hubs, hubs.map(h => h.percentage / 100));
        const hubPoint = polygonParentLayer.shapes.find((s): s is Point => s.type === 'point' && s.id === hubWeight.id);
        if (!hubPoint) continue;
        
        if (Math.random() < 0.5) {
          // Origin is random/meeting point, destination is hub
          origin = getOriginDestination(true, hasWheelchairPassenger);
          destination = makePoint(hubPoint.coordinates, t('generateRidesDrawer.destinationLabel', { index: i + 1 }), '#00FF00');
        } else {
          // Origin is hub, destination is random/meeting point
          origin = makePoint(hubPoint.coordinates, t('generateRidesDrawer.originLabel', { index: i + 1 }), '#FF0000');
          destination = getOriginDestination(false, hasWheelchairPassenger);
        }
      } else {
        // P2P: both random/meeting points
        origin = getOriginDestination(true, hasWheelchairPassenger);
        destination = getOriginDestination(false, hasWheelchairPassenger);
      }
      // Request time
      const requestTime = weightedRandomTime(startDateObj, endDateObj, peaks);
      // Use the correct ride pattern for this trip
      const patternToUse = ridePattern;
      const rideType = weightedRandom(rideTypes, rideTypes.map(r => r.probability)).type;
      // Points must have unique IDs
      origin.id = uuidv4();
      destination.id = uuidv4();
      points.push(origin, destination);
      // Line (trip)
      const rideId = t('generateRidesDrawer.tripLabel', { index: i + 1 });
      const userId = `user-${i + 1}`;
      
      const requestTimeString = requestTime instanceof Date
        ? formatDateAsWallTime(requestTime)
        : requestTime;
      
      const line: Line & { originPointId: string; destPointId: string } = {
        id: uuidv4(),
        type: 'line',
        name: rideId,
        ride_id: rideId,
        user_id: userId,
        colour: '#0000FF',
        description: '',
        opacity: '1',
        borderColour: '#0000FF',
        borderWidth: 2,
        isLocked: false,
        coordinates: [origin.coordinates, destination.coordinates],
        arrowStyle: 'arrow',
        time: requestTimeString,
        numPassengers,
        standard_seat: standardSeat,
        wheelchair: wheelchair,
        request_type: rideType,
        originPointId: origin.id,
        destPointId: destination.id,
        ...lineAddressPlaceholders({}, i + 1),
      };
      lines.push(line);
    }
    // Add new layer
    const newLayerId = global.mapLayers.length > 0 ? Math.max(...global.mapLayers.map(l => l.layerId)) + 1 : 0;
    const newLayer = {
      layerId: newLayerId,
      layerName: t('generateRidesDrawer.generatedLayerName', { index: newLayerId + 1 }),
      hidden: false,
      shapes: [...points, ...lines],
    };
    global.setLayers((prev: any) => [...prev, newLayer]);
    global.setActiveLayerId(newLayerId);
    
    // Track successful trip generation completion
    logEvent({
      category: 'TripGeneration',
      action: 'TripGenerationCompleted',
      label: 'Trip Generation Completed Successfully',
      polygonId: selectedPolygonId || 'none',
      polygonName: sortedPolygons.find(p => p.id === selectedPolygonId)?.name || 'none',
      numberOfRides: numTrips,
      generatedPoints: points.length,
      generatedLines: lines.length,
      newLayerId: newLayerId,
      newLayerName: newLayer.layerName,
      hubCount: numHubs,
        ridePattern: effectiveRidePattern,
      peaksCount: peaks.length,
      startDate: combineDateWithTime(DEFAULT_SIMULATION_DATE, startTimeStr),
      endDate: combineDateWithTime(DEFAULT_SIMULATION_DATE, endTimeStr),
      useMeetingPointsOnly: useMeetingPointsOnly,
      meetingPointLayerId: selectedMeetingPointLayerId || 'none',
      meetingPointLayerName: meetingPointLayers.find(l => l.id === selectedMeetingPointLayerId)?.name || 'none',
      meetingPointCount: getMeetingPoints.length
    });
    
    setIsGenerating(false);
    onClose();
  };

  const stepKeys = ['basics', 'peaks', 'passengers', 'zone', 'review'] as const;
  const steps = stepKeys.map(k => t(`generateRidesDrawer.steps.${k}`));

  const sectionCardSx = (theme: Theme) => ({
    mb: 2,
    p: 2,
    borderRadius: 2.5,
    ...frostedDrawerCardSx(theme, 0.14),
  });
  const [step, setStep] = useState(0);
  const canProceed = () => {
    // Add validation logic for each step here
    if (step === 0) {
      // Only require numberOfRides, startDate, and endDate in Basics
      return rideGenParams.numberOfRides && rideGenParams.startTime && rideGenParams.endTime;
    }
    if (step === 3) {
      // In Zone step, require polygon selection if polygons exist, or 2+ hubs if no polygon
      // Also require meeting point layer selection if meeting points mode is enabled
      const basicValidation = polygons.length > 0 
        ? (selectedPolygonId !== undefined || (rideGenParams.hubWeights && rideGenParams.hubWeights.length >= 2))
        : true;
      
      const meetingPointsValidation = useMeetingPointsOnly 
        ? selectedMeetingPointLayerId !== '' 
        : true;
      
      return basicValidation && meetingPointsValidation;
    }
    // For the Review step, require polygon selection, numberOfRides, start and end dates, or 2+ hubs if no polygon
    // Also require meeting point layer selection if meeting points mode is enabled
    if (step === steps.length - 1) {
      const basicValidation = polygons.length > 0
        ? (
            (selectedPolygonId && rideGenParams.numberOfRides && rideGenParams.startTime && rideGenParams.endTime) ||
            (rideGenParams.hubWeights && rideGenParams.hubWeights.length >= 2 && rideGenParams.numberOfRides && rideGenParams.startTime && rideGenParams.endTime)
          )
        : rideGenParams.numberOfRides && rideGenParams.startTime && rideGenParams.endTime;
      
      const meetingPointsValidation = useMeetingPointsOnly 
        ? selectedMeetingPointLayerId !== '' 
        : true;
      
      return basicValidation && meetingPointsValidation;
    }
    // Add more validation as needed for other steps
    return true;
  };
  const handleNext = () => setStep(s => Math.min(s + 1, steps.length - 1));
  const handleBack = () => setStep(s => Math.max(s - 1, 0));

  // Track step navigation
  const handleStepNavigation = (direction: 'next' | 'back', newStep: number) => {
    logEvent({
      category: 'TripGeneration',
      action: `Step${direction === 'next' ? 'Next' : 'Back'}`,
      label: `Trip Generation Step ${direction === 'next' ? 'Next' : 'Back'}`,
      currentStep: step,
      newStep: newStep,
      stepName: steps[newStep],
      canProceed: canProceed()
    });
  };

  const handleNextWithTracking = () => {
    const newStep = Math.min(step + 1, steps.length - 1);
    handleStepNavigation('next', newStep);
    setStep(newStep);
  };

  const handleBackWithTracking = () => {
    const newStep = Math.max(step - 1, 0);
    handleStepNavigation('back', newStep);
    setStep(newStep);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      PaperProps={{
        sx: (theme) => ({
          width: 640,
          maxWidth: 'calc(100vw - 65px)',
          p: 3,
          pr: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 80px)', // Adjust height to account for logo
          top: 80, // Position below logo
          boxSizing: 'border-box',
          right: 65,
          ...frostedDrawerPaperSx(theme),
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          overflow: 'hidden',
          zIndex: 2201,
        }),
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        sx={(theme) => ({
          px: 2,
          py: 1,
          borderRadius: 2,
          ...frostedDrawerHeaderSx(theme, 0.22),
        })}
      >
        <Typography variant="h6" fontWeight={700}>{t('generateRidesDrawer.title')}</Typography>
        <IconButton onClick={onClose}><Icon size={20} name="xmark" /></IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Stepper
        activeStep={step}
        alternativeLabel
        sx={(theme) => ({
          mb: 2.5,
          '& .MuiStepLabel-label': {
            mt: 0.6,
            fontWeight: 500,
            color: opsColors.neutral.inkSoft,
          },
          '& .MuiStepLabel-label.Mui-active': {
            color: opsColors.neutral.ink,
            fontWeight: 600,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: opsColors.neutral.ink,
          },
          '& .MuiStepConnector-line': {
            borderColor: alpha(opsColors.neutral.inkSoft, 0.28),
          },
          '& .MuiStepIcon-root': {
            color: alpha(opsColors.neutral.inkSoft, 0.4),
          },
          '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
            color: opsColors.brand.primary,
          },
        })}
      >
        {steps.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>
      {/* Main content area with extra bottom padding for sticky footer */}
      <Box flex={1} overflow="auto" sx={{ pb: 8.5 }}>
        {step === 0 && (
          <Box>
            {/* Default Date Info Banner */}
            <Box sx={(theme) => ({ ...sectionCardSx(theme), ...frostedDrawerCardSx(theme, 0.18) })}>
              <Typography variant="body2" color="primary">
                <b>{t('generateRidesDrawer.defaultDateBanner.label')}</b>{' '}
                <Trans i18nKey="generateRidesDrawer.defaultDateBanner.body" ns="mapCore" values={{ date: DEFAULT_SIMULATION_DATE }} components={[<b />]} />
              </Typography>
              <Typography variant="caption" color="textSecondary">
                <Trans i18nKey="generateRidesDrawer.defaultDateBanner.note" ns="mapCore" components={[<b />]} />
              </Typography>
            </Box>
            {/* Basics Step: Number of Rides, Start/End Times */}
            <Typography variant="h6" sx={{ mb: 1.25 }}>{t('generateRidesDrawer.basicsHeading')}</Typography>
            <Box sx={sectionCardSx}>
              <TextField
                label={t('generateRidesDrawer.numberOfRides')}
                type="number"
                fullWidth
                margin="normal"
                value={rideGenParams.numberOfRides || ''}
                onChange={e => {
                  const oldValue = rideGenParams.numberOfRides;
                  const newValue = Number(e.target.value);
                  setRideGenParams({ ...rideGenParams, numberOfRides: newValue });

                  // Track number of rides parameter change
                  logEvent({
                    category: 'TripGeneration',
                    action: 'NumberOfRidesChanged',
                    label: 'Number of Rides Parameter Changed',
                    oldValue: oldValue || 0,
                    newValue: newValue,
                    step: step
                  });
                }}
              />
              <Box display="flex" gap={2} sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <TextField
                  label={t('generateRidesDrawer.startTime')}
                  type="time"
                  fullWidth
                  margin="normal"
                  value={rideGenParams.startTime || '06:00'}
                  onChange={e => {
                    setRideGenParams({ ...rideGenParams, startTime: e.target.value });
                    logEvent({
                      category: 'TripGeneration',
                      action: 'StartTimeChanged',
                      label: 'Start Time Parameter Changed',
                      oldValue: rideGenParams.startTime || 'none',
                      newValue: e.target.value,
                      step: step
                    });
                  }}
                  inputProps={{ step: 300 }} // 5 min steps
                />
                <TextField
                  label={t('generateRidesDrawer.endTime')}
                  type="time"
                  fullWidth
                  margin="normal"
                  value={rideGenParams.endTime || '22:00'}
                  onChange={e => {
                    setRideGenParams({ ...rideGenParams, endTime: e.target.value });
                    logEvent({
                      category: 'TripGeneration',
                      action: 'EndTimeChanged',
                      label: 'End Time Parameter Changed',
                      oldValue: rideGenParams.endTime || 'none',
                      newValue: e.target.value,
                      step: step
                    });
                  }}
                  inputProps={{ step: 300 }}
                />
              </Box>
            </Box>
          </Box>
        )}
        {step === 1 && (
          <Box>
            {/* Peaks Step: Peak Preset Selector, Peaks Editor, Ride Time Distribution */}
            
            {/* Peak Preset Selector */}
            <PeakPresetSelector
              selectedPreset={selectedPreset}
              onPresetChange={handlePresetChange}
              startTime={startTime}
              endTime={endTime}
              interval={15}
            />
            
            {/* Advanced Peak Configuration */}
            <PeaksEditor 
              peaks={peaks} 
              setPeaks={setPeaks} 
              timeLabels={timeLabels} 
              defaultPeak={defaultPeak}
              selectedPreset={selectedPreset}
              onPresetChange={handlePresetChange}
              expanded={accordionExpanded}
              onExpandedChange={setAccordionExpanded}
            />
            
            <RideTimeDistributionChart peaks={peaks} startTime={startTime} endTime={endTime} interval={15} />
          </Box>
        )}
        {step === 2 && (
          <Box>
            {/* Passengers Step: Ride Types, Passenger Types, Number of Passengers */}
            <RideTypesSlider rideTypes={rideTypes} setRideTypes={setRideTypes} />
            <PassengerTypesEditor passengerTypes={passengerTypes} setPassengerTypes={setPassengerTypes} />
            <NumberOfPassengersEditor numberOfPassengers={numberOfPassengers} setNumberOfPassengers={setNumberOfPassengers} />
          </Box>
        )}
        {step === 3 && (
          <Box>
            {/* Zone Step: Polygon, Ride Pattern, Hubs & Weightings */}
            <Typography variant="h6" sx={{ mb: 1.25 }}>{t('generateRidesDrawer.zoneHeading')}</Typography>
            <FormControl fullWidth margin="normal" required={!!polygons.length} error={false}>
              <InputLabel required={!!polygons.length}>{t('generateRidesDrawer.selectPolygon')}</InputLabel>
              <Select
                value={selectedPolygonId === undefined || selectedPolygonId === null ? '' : selectedPolygonId}
                label={t('generateRidesDrawer.selectPolygon')}
                MenuProps={selectMenuProps}
                onChange={e => {
                  const value = e.target.value as string;
                  const oldPolygonId = selectedPolygonId;
                  setSelectedPolygonId(value === '' ? '' : value);
                  
                  // Track polygon selection change
                  logEvent({
                    category: 'TripGeneration',
                    action: 'PolygonSelected',
                    label: 'Polygon Selected for Trip Generation',
                    oldPolygonId: oldPolygonId || 'none',
                    newPolygonId: value || 'none',
                    polygonName: sortedPolygons.find(p => p.id === value)?.name || 'none',
                    step: step
                  });
                  
                  // No forced setRidePattern here; handled in render
                }}
                renderValue={selected => {
                  if (!selected) return t('generateRidesDrawer.noneOption');
                  const found = sortedPolygons.find(p => p.id === selected);
                  return found ? found.name : t('generateRidesDrawer.noneOption');
                }}
              >
                <MenuItem value="">{t('generateRidesDrawer.noneOption')}</MenuItem>
                {sortedPolygons.map((polygon, index) => (
                  <MenuItem key={polygon.id} value={polygon.id}>
                    {polygon.name}
                    {index === 0 && t('generateRidesDrawer.mostRecentSuffix')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <RidePatternEditor
              ridePattern={ridePatternState.pattern}
              setRidePattern={p => {
                if (!ridePatternState.disabled) setRidePattern(p);
              }}
              disabled={ridePatternState.disabled}
              numHubs={numHubs}
              onlyH2HDisabled={ridePatternState.onlyH2HDisabled}
            />
            {/* Only show points from the same layer as the selected polygon, or all points if no polygon is selected */}
            {(() => {
              let availablePoints: { id: string; name: string }[] = [];
              if (selectedPolygonId && global) {
                const polygonLayer = global.mapLayers.find(l => l.shapes.some(s => s.type === 'polygon' && s.id === selectedPolygonId));
                if (polygonLayer) {
                  availablePoints = polygonLayer.shapes.filter((s): s is Point => s.type === 'point').map((p: Point) => ({ id: p.id, name: p.name || p.id }));
                }
              } else if (global) {
                // No polygon selected: show all points from all layers
                availablePoints = global.mapLayers.flatMap(l => (l.shapes || []).filter((s): s is Point => s.type === 'point').map((p: Point) => ({ id: p.id, name: p.name || p.id })));
              }
              return (
                <HubsEditor
                  availablePoints={availablePoints}
                  hubs={rideGenParams.hubWeights}
                  setHubs={hubs => {
                    setRideGenParams((prev: any) => ({ ...prev, hubWeights: hubs }));
                    setForceUpdate(x => x + 1);
                  }}
                />
              );
            })()}
            
            {/* Meeting Points Section */}
            <Box sx={(theme) => ({ ...sectionCardSx(theme), mt: 3 })}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {t('generateRidesDrawer.meetingPoints.title')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {t('generateRidesDrawer.meetingPoints.description')}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={useMeetingPointsOnly}
                    onChange={(e) => {
                      setUseMeetingPointsOnly(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedMeetingPointLayerId('');
                      }
                      logEvent({
                        category: 'TripGeneration',
                        action: 'MeetingPointsToggle',
                        label: 'Meeting Points Mode Toggled',
                        enabled: e.target.checked,
                        step: step
                      });
                    }}
                  />
                }
                label={t('generateRidesDrawer.meetingPoints.useMeetingPointsOnly')}
                sx={{ mb: 2 }}
              />

              {useMeetingPointsOnly && (
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>{t('generateRidesDrawer.meetingPoints.selectLayer')}</InputLabel>
                  <Select
                    value={selectedMeetingPointLayerId}
                    label={t('generateRidesDrawer.meetingPoints.selectLayer')}
                    MenuProps={selectMenuProps}
                    onChange={(e) => {
                      const value = e.target.value as string;
                      setSelectedMeetingPointLayerId(value);
                      logEvent({
                        category: 'TripGeneration',
                        action: 'MeetingPointLayerSelected',
                        label: 'Meeting Point Layer Selected',
                        layerId: value,
                        layerName: meetingPointLayers.find(l => l.id === value)?.name || 'unknown',
                        step: step
                      });
                    }}
                    renderValue={selected => {
                      if (!selected) return t('generateRidesDrawer.meetingPoints.selectLayerPlaceholder');
                      const found = meetingPointLayers.find(l => l.id === selected);
                      return found ? t('generateRidesDrawer.meetingPoints.layerSummary', { name: found.name, count: found.pointCount }) : t('generateRidesDrawer.meetingPoints.unknownLayer');
                    }}
                  >
                    {meetingPointLayers.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          {t('generateRidesDrawer.meetingPoints.noLayers')}
                        </Typography>
                      </MenuItem>
                    ) : (
                      meetingPointLayers.map((layer) => (
                        <MenuItem key={layer.id} value={layer.id}>
                          <Box>
                            <Typography variant="body1">{layer.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('generateRidesDrawer.meetingPoints.layerPoints', { count: layer.pointCount })}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>
        )}
        {step === 4 && (
          <Box>
            {/* Review Step: Summary and Generate Button */}
            <Typography variant="h6" sx={{ mb: 1.25 }}>{t('generateRidesDrawer.review.heading')}</Typography>
            <Box sx={(theme) => ({ ...sectionCardSx(theme), mt: 0.5 })}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} name="map" />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.polygon')}</b> {sortedPolygons.find(p => p.id === selectedPolygonId)?.name || t('generateRidesDrawer.review.na')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} name="car-side" />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.numberOfRides')}</b> {rideGenParams.numberOfRides || t('generateRidesDrawer.review.na')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} name="clock" />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.startTime')}</b> {rideGenParams.startTime || t('generateRidesDrawer.review.na')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} name="clock" />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.endTime')}</b> {rideGenParams.endTime || t('generateRidesDrawer.review.na')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} d={ICON_FALLBACKS.TimelineOutlined} />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.peaks')}</b> {peaks.length > 0 ? peaks.map((p, i) => t('generateRidesDrawer.review.peakSummary', { index: i + 1, time: p.peak_time, effect: p.effect, left: p.width_left, right: p.width_right })).join('; ') : t('generateRidesDrawer.review.none')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} d={ICON_FALLBACKS.SwapHorizOutlined} />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.rideTypeSplit')}</b> {t('generateRidesDrawer.review.rideTypeSplitValue', { departure: Math.round((rideTypes.find(r => r.type === 'departure_fixed')?.probability || 0) * 100), arrival: Math.round((rideTypes.find(r => r.type === 'arrival_fixed')?.probability || 0) * 100) })}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} name="users" />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.passengerTypes')}</b> {passengerTypes.map(pt => `${pt.passenger_type} (${Math.round((pt.probability || 0) * 100)}%, ${pt.processing_time}s)`).join('; ')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} name="users" />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.passengersPerRide')}</b> {numberOfPassengers.map(np => `${np.number_of_passengers} (${Math.round((np.probability || 0) * 100)}%)`).join('; ')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} d={ICON_FALLBACKS.HubOutlined} />
                  <Typography variant="body1"><b>{t('generateRidesDrawer.review.hubs')}</b> {rideGenParams.hubWeights && rideGenParams.hubWeights.length > 0 ? rideGenParams.hubWeights.map((h: any) => `${h.name} (${h.percentage}%)`).join('; ') : t('generateRidesDrawer.review.none')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon size={20} d={ICON_FALLBACKS.AltRouteOutlined} />
                  <Typography variant="body1">
                    <b>{t('generateRidesDrawer.review.tripTypeSplit')}</b> {t('generateRidesDrawer.review.tripTypeSplitValue', { p2p: ridePatternState.pattern.p2p, p2h: ridePatternState.pattern.p2h, h2h: ridePatternState.pattern.h2h })}
                  </Typography>
                </Stack>
                {useMeetingPointsOnly && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon size={20} d={ICON_FALLBACKS.HubOutlined} />
                    <Typography variant="body1">
                      <b>{t('generateRidesDrawer.review.meetingPointsLabel')}</b> {t('generateRidesDrawer.review.meetingPointsValue', { layerName: meetingPointLayers.find(l => l.id === selectedMeetingPointLayerId)?.name || t('generateRidesDrawer.review.noLayerSelected'), count: meetingPointLayers.find(l => l.id === selectedMeetingPointLayerId)?.pointCount || 0 })}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
      {/* Sticky footer for navigation buttons */}
      <Box
        sx={(theme) => ({
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: alpha(opsColors.neutral.panel, 0.92),
          px: 2.25,
          py: 1.25,
          minHeight: 72,
          borderTop: '1px solid',
          borderColor: opsColors.neutral.line,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.75,
          mt: 0.5,
        })}
      >
        {step > 0 && (
          <Button
            onClick={handleBackWithTracking}
            sx={(theme) => ({
              ...secondaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('generateRidesDrawer.footer.back')}
          </Button>
        )}
        {step < steps.length - 1 && (
          <Button
            onClick={handleNextWithTracking}
            disabled={!canProceed()}
            sx={(theme) => ({
              ...secondaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('generateRidesDrawer.footer.next')}
          </Button>
        )}
        {step === steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleGenerateTrips}
            disabled={!canProceed() || isGenerating}
            sx={(theme) => ({
              ...primaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('generateRidesDrawer.footer.generate')}
          </Button>
        )}
      </Box>

      {/* Full-drawer loading overlay shown while trips are being generated */}
      {isGenerating && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              px: 4,
              py: 4,
              maxWidth: 300,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.97)',
              boxShadow: '0 28px 56px rgba(58, 58, 72, 0.14), 0 0 0 1px rgba(238, 115, 52, 0.14)',
            }}
          >
            {/* Pure CSS spinner — immune to MUI em/font-size sizing issues inside Drawer */}
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                border: '5px solid rgba(238, 115, 52, 0.18)',
                borderTopColor: '#EE7334',
                flexShrink: 0,
                animation: 'generateRidesSpin 0.75s linear infinite',
                '@keyframes generateRidesSpin': {
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", "Roboto", sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#3A3A48',
                  letterSpacing: '0.03em',
                }}
              >
                {t('generateRidesDrawer.loadingOverlay.title')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  color: 'rgba(58, 58, 72, 0.62)',
                  mt: 0.75,
                  lineHeight: 1.5,
                }}
              >
                {t('generateRidesDrawer.loadingOverlay.body', { total: (rideGenParams.numberOfRides || 0).toLocaleString() })}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default GenerateRidesDrawer; 
